import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import moment from "moment";
import "moment/locale/tr";
import { useEffect, useState } from "react";
import { BulkSnoozePreviewResponse } from "../../Services/SnoozeService";
import { getRemoteText, getRemoteValue, getRemoteValueVariable } from "../../Utils/firebase";
import { FormatAmountSymbol } from "../FormatData/FormatAmount";

export interface BulkSnoozeSummaryModalProps {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  /** Selected snooze calendar day: `YYYY-MM-DD` (local) or ISO string; used as Turkish long date fallback. */
  paymentDate: string;
  preview: BulkSnoozePreviewResponse;
  onEditPayments?: () => void;
  onPayPressed: (paymentPlanIds: number[]) => void;
  /** When set, pay action sends only these plan ids; summary rows always use `preview` totals. */
  selectedPaymentPlanIds?: number[];
}

const BORDER_GRAY = "#E2E8F0";
const TEXT_PRIMARY = "#191B1E";
const TEXT_MUTED = "#64748B";

const BulkSnoozeSummaryModal = ({
  open,
  onClose,
  onBack,
  paymentDate,
  preview,
  onEditPayments,
  onPayPressed,
  selectedPaymentPlanIds,
}: BulkSnoozeSummaryModalProps) => {
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (open) setAgreed(false);
  }, [open]);

  const postponedCount = preview.calculatedPaymentPlanCount ?? 0;

  const title = getRemoteValue("bulk_snooze_summary_title") || "Erteleme Özeti";
  const labelNewDate =
    getRemoteValue("bulk_snooze_summary_new_payment_date") || "Yeni Ödeme Tarihi :";
  const labelCount =
    getRemoteValue("bulk_snooze_summary_postponed_count") ||
    "Ertelenen İşlem Sayısı:";
  const labelAmount =
    getRemoteValue("bulk_snooze_summary_postponed_amount") ||
    "Ertelenen İşlem Tutarı:";
  const labelServiceFee =
    getRemoteValue("bulk_snooze_summary_service_fee") || "Erteleme Hizmet Bedeli";
  const countText = getRemoteValueVariable("amount_x", (postponedCount).toString());
  // CRL-3241 (new-design.html): pay button carries the fee amount
  const payButtonLabel = getRemoteText(
    "snooze_pay_and_defer_x",
    "{val} öde ve ertele",
    FormatAmountSymbol(preview.totalSnoozeFee)
  );
  const agreementPrefix =
    getRemoteValue("bulk_snooze_agreement_prefix") || "Colendi ";
  const agreementLinkText =
    getRemoteValue("bulk_snooze_agreement_link") || "Erteleme Sözleşmesi'ni";
  const agreementSuffix =
    getRemoteValue("bulk_snooze_agreement_suffix") || " okudum, onaylıyorum.";


  const postponedAmount = preview.totalAmount;
  const serviceFee = preview.totalSnoozeFee;
  const dateIsoForLabel = paymentDate;

  const newPaymentDateLabel = (() => {
    const s = dateIsoForLabel?.trim() ?? "";
    if (!s) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return moment(s, "YYYY-MM-DD", true).locale("tr").format("D MMMM YYYY");
    }
    return moment(s).locale("tr").format("D MMMM YYYY");
  })();

  const handleClose = () => {
    setAgreed(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="body"
      PaperProps={{
        sx: {
          borderRadius: "24px",
          m: 2,
          maxWidth: "575px",
          width: "100%",
        },
      }}
    >
      <Stack direction='column' spacing={'2.5px'}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 4,
            top: 4,
            zIndex: 999,
            color: (theme) => theme.palette.grey[700],
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            position: "relative",
            pt: 3,
            pb: 1,
            px: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton
            aria-label="back"
            onClick={onBack}
            sx={{
              position: "absolute",
              left: 4,
              color: TEXT_PRIMARY,
            }}
          >
            <ArrowBackOutlinedIcon sx={{ fontSize: 22 }} />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: { xs: "16px", md: "20px" },
              color: TEXT_PRIMARY,
              px: { xs: 3, md: 6 },
            }}
          >
            {title}
          </Typography>
        </Box>
      </Stack>
      <DialogContent sx={{ pt: 2, px: { xs: 1.5, md: 2.5 }, pb: 3 }} className="max-w-[480px] place-self-center">
        <Stack spacing={2} className="w-full max-w-[450px] px-[20px] place-self-center">
          <SummaryRow label={labelNewDate} value={newPaymentDateLabel} />

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: { xs: "14px", md: "16px" }, color: TEXT_PRIMARY }}>
              {labelCount}
            </Typography>
            <Stack direction="row" alignItems="center" className="items-center" spacing={0.5}>
              <Typography
                component="span"
                onClick={onEditPayments}
                onKeyDown={
                  onEditPayments
                    ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onEditPayments();
                      }
                    }
                    : undefined
                }
                role={onEditPayments ? "button" : undefined}
                tabIndex={onEditPayments ? 0 : undefined}
                sx={{
                  fontSize: { xs: "14px", md: "16px" },
                  fontWeight: 600,
                  color: TEXT_PRIMARY,
                  ...(onEditPayments && {
                    cursor: "pointer",
                    "&:focus-visible": {
                      outline: "2px solid transparent",
                      outlineOffset: 2,
                      borderRadius: "4px",
                    },
                  }),
                }}
              >
                {countText}
              </Typography>
              {onEditPayments && (
                <IconButton
                  size="small"
                  aria-label="edit postponed count"
                  onClick={onEditPayments}
                  sx={{ color: TEXT_PRIMARY }}
                >
                  <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}/bulk_snooze_edit.svg`}
                    alt=""
                    sx={{ width: { xs: 16, md: 20 }, height: { xs: 16, md: 20 }, display: "block" }}
                  />
                </IconButton>
              )}
            </Stack>
          </Stack>

          <SummaryRow
            label={labelAmount}
            value={FormatAmountSymbol(postponedAmount)}
          />

          <SummaryRow
            label={labelServiceFee}
            value={FormatAmountSymbol(serviceFee)}
          />

          {/* CRL-3241 (new-design.html ds-total): amount + fee as the focal number */}
          <Box
            sx={{
              borderTop: `1px solid ${BORDER_GRAY}`,
              borderBottom: `1px solid ${BORDER_GRAY}`,
              py: 2,
              my: 0.5,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography sx={{ fontSize: { xs: "13px", md: "14px" }, color: TEXT_MUTED }}>
                {getRemoteText("snooze_new_total", "Yeni Ödenecek Toplam")}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "26px", md: "32px" },
                  fontWeight: 700,
                  color: TEXT_PRIMARY,
                }}
              >
                {FormatAmountSymbol(postponedAmount + serviceFee)}
              </Typography>
            </Stack>
          </Box>

        </Stack>
        <div className="h-[20px]" />
        <FormControlLabel
          control={
            <Checkbox
              checked={agreed}
              onChange={(_, v) => setAgreed(v)}
              sx={{
                color: BORDER_GRAY,
                p: 0,
                pr: 1,
                "&.Mui-checked": { color: "#191B1E" },
              }}
            />
          }
          label={
            <Typography component="span" sx={{ fontSize: { xs: "13px", md: "15px" }, color: TEXT_PRIMARY }}>
              {agreementPrefix}
              <Link
                href={'https://documents.colendilabs.com/credit-docs/moneypay-agreements/Dijital-Alisveris-Kodu-Erteleme-Hizmeti-Onay-Sozlesmesi-03-10-2024.html'}
                underline="always"
                target="_blank"
                sx={{ color: TEXT_PRIMARY, fontWeight: 500 }}
              >
                {agreementLinkText}
              </Link>
              {agreementSuffix}
            </Typography>
          }
          sx={{ alignItems: "center", ml: 0, mr: 0 }}
        />
        <div className="h-[14px]" />
        <Button
          fullWidth
          variant="contained"
          disabled={!agreed}
          onClick={() => {
            const ids =
              selectedPaymentPlanIds != null && selectedPaymentPlanIds.length > 0
                ? preview.paymentPlans
                  .filter(
                    (p) =>
                      selectedPaymentPlanIds.includes(p.paymentPlanId) &&
                      p.snoozeAvailable
                  )
                  .map((p) => p.paymentPlanId)
                : preview.paymentPlans
                  .filter((p) => p.snoozeAvailable && p.selected !== false)
                  .map((p) => p.paymentPlanId);
            onPayPressed(ids);
          }}
          sx={{
            py: 1.35,
            borderRadius: "15px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: { xs: "15px", md: "18px" },
            boxShadow: "none",
            bgcolor: agreed ? "#191B1E" : "#CBD5E1",
            color: "#fff",
            "&:hover": {
              boxShadow: "none",
              bgcolor: agreed ? "#1E293B" : "#CBD5E1",
            },
            "&.Mui-disabled": {
              bgcolor: "#CBD5E1",
              color: "#fff",
            },
          }}
        >
          {payButtonLabel}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Typography sx={{ fontSize: { xs: "14px", md: "16px" }, color: TEXT_PRIMARY }}>{label}</Typography>
      <Typography sx={{ fontSize: { xs: "14px", md: "16px" }, fontWeight: 600, color: TEXT_PRIMARY }}>
        {value}
      </Typography>
    </Stack>
  );
}

export default BulkSnoozeSummaryModal;
